// Gestão da Central de Ajuda (exclusiva do ADMIN). Editor simples em Markdown com botões de formatação e
// pré-visualização (o ADMIN não precisa conhecer a sintaxe), estados rascunho/publicado, histórico de
// versões com restauração, associação a ambiente/permissões/cargos e anexo de imagens (servidas com gate).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Archive, ArchiveRestore, BookOpen, Bold, Eye, Image as ImageIcon, Italic, Link2, List, Loader2,
  Plus, RotateCcw, Save, Table as TableIcon, Type,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { HelpMarkdown } from "@/lib/helpMarkdown";
import { helpImageUrl } from "@/lib/helpApi";
import {
  archiveAdminHelp, createAdminHelp, getAdminHelp, listAdminHelp, publishAdminHelp, restoreAdminHelp,
  reviewAdminHelp, updateAdminHelp, uploadAdminHelpImage,
  type AdminHelpDetail, type AdminHelpSummary, type HelpArticlePayload,
} from "@/lib/adminHelpApi";
import { cn } from "@/lib/utils";

const ENVIRONMENTS = [
  { value: "STAFF", label: "Staff (back-office)" },
  { value: "CLIENT", label: "Cliente" },
  { value: "DRIVER", label: "Entregador" },
];
const CATEGORIES = [
  { value: "GETTING_STARTED", label: "Comece aqui" },
  { value: "MODULE_MANUAL", label: "Manual por módulo" },
  { value: "ROLE_GUIDE", label: "Guia de função" },
];
const ROLES = [
  "ADMIN", "ADMINISTRATIVO", "PLANEJADOR", "VENDEDOR", "COMPRADOR", "CONFERENTE",
  "EXPEDICAO", "PRODUCAO", "SEPARACAO", "ENTREGADOR", "CLIENT",
];

type FormState = {
  slug: string;
  environment: string;
  category: string;
  moduleKey: string;
  screenKey: string;
  displayOrder: string;
  title: string;
  summary: string;
  contentMarkdown: string;
  requiredPermissions: string;
  roleKeys: string[];
};

const EMPTY_FORM: FormState = {
  slug: "", environment: "STAFF", category: "MODULE_MANUAL", moduleKey: "", screenKey: "",
  displayOrder: "100", title: "", summary: "", contentMarkdown: "", requiredPermissions: "", roleKeys: [],
};

export default function AdminHelpArticles() {
  const [list, setList] = useState<AdminHelpSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [detail, setDetail] = useState<AdminHelpDetail | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const refreshList = useCallback(() => {
    setLoadingList(true);
    listAdminHelp().then(setList).catch(() => setList([])).finally(() => setLoadingList(false));
  }, []);

  useEffect(() => { refreshList(); }, [refreshList]);

  const loadDetail = (id: string) => {
    getAdminHelp(id).then((d) => {
      setDetail(d);
      setIsNew(false);
      setPreview(false);
      setForm({
        slug: d.slug,
        environment: d.environment,
        category: d.category,
        moduleKey: d.moduleKey ?? "",
        screenKey: d.screenKey ?? "",
        displayOrder: String(d.displayOrder),
        title: d.hasDraft ? (d.draftTitle ?? d.title) : d.title,
        summary: (d.hasDraft ? d.draftSummary : d.summary) ?? "",
        contentMarkdown: (d.hasDraft ? d.draftContentMarkdown : d.publishedContentMarkdown) ?? "",
        requiredPermissions: d.requiredPermissions.join(", "),
        roleKeys: d.roleKeys,
      });
    }).catch((e) => toast.error(getApiErrorMessage(e)));
  };

  const startNew = () => {
    setDetail(null);
    setIsNew(true);
    setPreview(false);
    setForm(EMPTY_FORM);
  };

  const payload = (): HelpArticlePayload => ({
    slug: isNew ? form.slug : undefined,
    environment: form.environment,
    category: form.category,
    moduleKey: form.moduleKey.trim() || null,
    screenKey: form.screenKey.trim() || null,
    displayOrder: form.displayOrder.trim() === "" ? null : Number(form.displayOrder),
    title: form.title.trim(),
    summary: form.summary.trim() || null,
    contentMarkdown: form.contentMarkdown,
    requiredPermissions: form.requiredPermissions.split(",").map((s) => s.trim()).filter(Boolean),
    roleKeys: form.roleKeys,
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Informe o título."); return; }
    if (isNew && !form.slug.trim()) { toast.error("Informe o identificador (slug)."); return; }
    setSaving(true);
    try {
      const saved = isNew ? await createAdminHelp(payload()) : await updateAdminHelp(detail!.id, payload());
      toast.success("Rascunho salvo.");
      refreshList();
      loadDetail(saved.id);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (fn: () => Promise<AdminHelpDetail>, okMsg: string) => {
    try {
      const d = await fn();
      toast.success(okMsg);
      refreshList();
      loadDetail(d.id);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const insert = (before: string, after = "", placeholder = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const hadSelection = end > start;
    const text = form.contentMarkdown;
    const selected = hadSelection ? text.slice(start, end) : placeholder;
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    setForm((f) => ({ ...f, contentMarkdown: next }));
    requestAnimationFrame(() => {
      ta.focus();
      if (hadSelection) {
        const pos = start + before.length + selected.length;
        ta.setSelectionRange(pos, pos);
      } else {
        // Sem seleção prévia: deixa o texto modelo selecionado para ser substituído ao digitar.
        ta.setSelectionRange(start + before.length, start + before.length + selected.length);
      }
    });
  };

  const onUploadImage = async (file: File) => {
    if (isNew || !detail) { toast.error("Salve o artigo antes de anexar imagens."); return; }
    try {
      const ref = await uploadAdminHelpImage(detail.id, file);
      // Insere a referência no conteúdo E registra a imagem localmente — sem recarregar o form (o reload
      // sobrescrevia o rascunho e apagava a referência recém-inserida). A imagem entra em LINHA PRÓPRIA.
      insert(`\n![${file.name}](${ref.id})\n`);
      setDetail((d) => (d ? { ...d, images: [...d.images, ref] } : d));
      toast.success("Imagem anexada. Referência inserida no conteúdo.");
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const toggleRole = (role: string) =>
    setForm((f) => ({
      ...f,
      roleKeys: f.roleKeys.includes(role) ? f.roleKeys.filter((r) => r !== role) : [...f.roleKeys, role],
    }));

  const editing = isNew || detail;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">Ajuda e Treinamento — gestão</h1>
        </div>
        <Button size="sm" onClick={startNew}><Plus className="mr-1 h-4 w-4" /> Novo artigo</Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4 lg:flex-row">
        {/* Lista */}
        <aside className="w-full shrink-0 lg:w-72">
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Artigos</h2>
          {loadingList ? <Loading /> : (
            <ul className="space-y-1">
              {list.map((a) => (
                <li key={a.id}>
                  <button type="button" onClick={() => loadDetail(a.id)}
                    className={cn("w-full rounded-lg border px-3 py-2 text-left text-sm",
                      detail?.id === a.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/60")}>
                    <p className="font-medium leading-tight">{a.title}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Badge tone={a.published ? "green" : "muted"}>{a.published ? "Publicado" : "Não publicado"}</Badge>
                      {a.hasDraft ? <Badge tone="amber">Rascunho</Badge> : null}
                      {a.archived ? <Badge tone="red">Arquivado</Badge> : null}
                      <Badge tone="muted">{a.environment}</Badge>
                    </div>
                  </button>
                </li>
              ))}
              {list.length === 0 ? <li className="text-sm text-muted-foreground">Nenhum artigo ainda.</li> : null}
            </ul>
          )}
        </aside>

        {/* Editor */}
        <section className="min-w-0 flex-1">
          {!editing ? (
            <p className="text-sm text-muted-foreground">Selecione um artigo à esquerda ou crie um novo.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Identificador (slug)">
                  <input disabled={!isNew} value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="ex.: caixas-rotina-conferente" className={inputCls} />
                </Field>
                <Field label="Título">
                  <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className={inputCls} />
                </Field>
                <Field label="Ambiente">
                  <select value={form.environment} onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))}
                    className={inputCls}>
                    {ENVIRONMENTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Seção">
                  <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className={inputCls}>
                    {CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Módulo (agrupa em Manuais)">
                  <input value={form.moduleKey} onChange={(e) => setForm((f) => ({ ...f, moduleKey: e.target.value }))}
                    placeholder="ex.: Controle de Caixas" className={inputCls} />
                </Field>
                <Field label="Tela (screenKey p/ Ajuda desta tela)">
                  <input value={form.screenKey} onChange={(e) => setForm((f) => ({ ...f, screenKey: e.target.value }))}
                    placeholder="ex.: /admin/caixas" className={inputCls} />
                </Field>
                <Field label="Ordem">
                  <input value={form.displayOrder} onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
                    inputMode="numeric" className={inputCls} />
                </Field>
                <Field label="Resumo">
                  <input value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                    className={inputCls} />
                </Field>
              </div>

              <Field label="Permissões exigidas (códigos separados por vírgula; vazio = geral do ambiente)">
                <input value={form.requiredPermissions}
                  onChange={(e) => setForm((f) => ({ ...f, requiredPermissions: e.target.value }))}
                  placeholder="ex.: CAIXAS.FINANCEIRO.VISUALIZAR" className={inputCls} />
              </Field>

              <Field label="Cargos relacionados (organiza 'Minha função'; não concede acesso)">
                <div className="flex flex-wrap gap-1.5">
                  {ROLES.map((r) => (
                    <button type="button" key={r} onClick={() => toggleRole(r)}
                      className={cn("rounded-full border px-2.5 py-1 text-xs",
                        form.roleKeys.includes(r) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>
                      {r}
                    </button>
                  ))}
                </div>
              </Field>

              {/* Editor de conteúdo */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-medium text-muted-foreground">Conteúdo (Markdown)</label>
                  <Button variant="ghost" size="sm" onClick={() => setPreview((p) => !p)}>
                    <Eye className="mr-1 h-4 w-4" /> {preview ? "Editar" : "Pré-visualizar"}
                  </Button>
                </div>
                {!preview && (
                  <div className="mb-1 flex flex-wrap gap-1">
                    <ToolBtn title="Título" onClick={() => insert("## ", "", "Título")}><Type className="h-4 w-4" /></ToolBtn>
                    <ToolBtn title="Negrito" onClick={() => insert("**", "**", "negrito")}><Bold className="h-4 w-4" /></ToolBtn>
                    <ToolBtn title="Itálico" onClick={() => insert("*", "*", "itálico")}><Italic className="h-4 w-4" /></ToolBtn>
                    <ToolBtn title="Lista" onClick={() => insert("- ", "", "item")}><List className="h-4 w-4" /></ToolBtn>
                    <ToolBtn title="Tabela" onClick={() => insert("\n| Coluna A | Coluna B |\n| --- | --- |\n| valor | valor |\n")}><TableIcon className="h-4 w-4" /></ToolBtn>
                    <ToolBtn title="Link interno" onClick={() => insert("[texto](/admin/...)")}><Link2 className="h-4 w-4" /></ToolBtn>
                    <ImageButton onFile={onUploadImage} disabled={isNew} />
                  </div>
                )}
                {preview ? (
                  <div className="min-h-[260px] rounded-lg border border-border p-3">
                    <HelpMarkdown
                      content={form.contentMarkdown}
                      handlers={{ resolveImageUrl: (imgId) => (detail ? helpImageUrl(detail.id, imgId) : null) }}
                    />
                  </div>
                ) : (
                  <textarea ref={textareaRef} value={form.contentMarkdown}
                    onChange={(e) => setForm((f) => ({ ...f, contentMarkdown: e.target.value }))}
                    rows={16} className="w-full rounded-lg border border-border bg-background p-3 font-mono text-sm outline-none focus:ring-2 focus:ring-primary" />
                )}
              </div>

              {/* Ações */}
              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                <Button onClick={save} disabled={saving}>
                  {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Salvar rascunho
                </Button>
                {detail ? (
                  <>
                    <Button variant="secondary" onClick={() => doAction(() => publishAdminHelp(detail.id), "Publicado.")}
                      disabled={!detail.hasDraft}>
                      Publicar
                    </Button>
                    {detail.archived ? (
                      <Button variant="ghost" onClick={() => doAction(() => archiveAdminHelp(detail.id, false), "Desarquivado.")}>
                        <ArchiveRestore className="mr-1 h-4 w-4" /> Desarquivar
                      </Button>
                    ) : (
                      <Button variant="ghost" onClick={() => doAction(() => archiveAdminHelp(detail.id, true), "Arquivado.")}>
                        <Archive className="mr-1 h-4 w-4" /> Arquivar
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => doAction(() => reviewAdminHelp(detail.id), "Revisão registrada.")}>
                      Marcar revisão
                    </Button>
                  </>
                ) : null}
              </div>

              {/* Versões */}
              {detail && detail.versions.length > 0 ? (
                <div className="border-t border-border pt-3">
                  <h3 className="mb-2 text-sm font-semibold">Histórico de versões</h3>
                  <ul className="space-y-1 text-sm">
                    {detail.versions.map((v) => (
                      <li key={v.id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5">
                        <span>
                          v{v.versionNumber} ·{" "}
                          {v.state === "PUBLISHED" ? "publicada" : v.state === "SUPERSEDED" ? "substituída" : "rascunho"} ·{" "}
                          <span className="text-muted-foreground">{v.authorEmail} ({v.authorRole})</span>
                        </span>
                        <Button variant="ghost" size="sm"
                          onClick={() => doAction(() => restoreAdminHelp(detail.id, v.id), "Versão restaurada como novo rascunho.")}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restaurar
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary disabled:opacity-60";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ToolBtn({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" title={title} onClick={onClick}
      className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
      {children}
    </button>
  );
}

function ImageButton({ onFile, disabled }: { onFile: (f: File) => void; disabled: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" title={disabled ? "Salve o artigo antes de anexar imagens" : "Inserir imagem"}
        disabled={disabled} onClick={() => ref.current?.click()}
        className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">
        <ImageIcon className="h-4 w-4" />
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
    </>
  );
}

function Badge({ tone, children }: { tone: "green" | "amber" | "red" | "muted"; children: React.ReactNode }) {
  const cls = tone === "green" ? "bg-emerald-100 text-emerald-700"
    : tone === "amber" ? "bg-amber-100 text-amber-700"
    : tone === "red" ? "bg-red-100 text-red-700"
    : "bg-muted text-muted-foreground";
  return <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-semibold", cls)}>{children}</span>;
}

function Loading() {
  return <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;
}
